import { useCallback, useEffect } from "react";
import { Socket } from "socket.io-client";

import { useVidyoStore } from "@/features/vidyo-call/utils/vidyo-store";
import {
  BreakoutSessionClosedPayload,
  BreakoutSessionsCreatedPayload,
  BreakoutSessionsDetailsPayload,
  BreakoutSessionsUpdatedPayload,
  ClientToServerEvents,
  ServerToClientEvents,
} from "@classview/shared-types";

type SubscribeToBreakoutSessionProps = {
  enabled: boolean;
  socketClient: Socket<ServerToClientEvents, ClientToServerEvents>;
};

/**
 * This hook is used to subscribe to breakout session changes
 */
export function useSubscribeToBreakoutSessionChanges({
  enabled,
  socketClient,
}: SubscribeToBreakoutSessionProps) {
  const parentSession = useVidyoStore(
    (state) => state.breakoutSession.parentSession,
  );
  const currentSessionId = useVidyoStore((state) => state.sessionId);

  /**
   * If the current session is a breakout session,
   * this is the parent session id
   * otherwise, this is the current session id
   */
  const sessionId =
    parentSession !== null ? parentSession.id : currentSessionId;

  const startBreakoutSession = useVidyoStore(
    (state) => state.breakoutSession.startBreakoutSession,
  );
  const closeBreakoutSession = useVidyoStore(
    (state) => state.breakoutSession.closeBreakoutSession,
  );
  const updateBreakoutSessionParticipants = useVidyoStore(
    (state) => state.breakoutSession.updateBreakoutSessionParticipants,
  );
  const moveParticipantToBreakoutSession = useVidyoStore(
    (state) => state.breakoutSession.moveParticipantToBreakoutSession,
  );

  const handleBreakoutSessionsCreated = useCallback(
    (payload: BreakoutSessionsCreatedPayload) => {
      startBreakoutSession(payload);
      console.log(
        "[useSubscribeToBreakoutSessionChanges] breakoutSessionsCreated",
      );
    },
    [startBreakoutSession],
  );

  const handleBreakoutSessionClosed = useCallback(
    (payload: BreakoutSessionClosedPayload) => {
      console.log(
        "[useSubscribeToBreakoutSessionChanges] breakoutSessionClosed",
      );
      closeBreakoutSession(payload);
    },
    [closeBreakoutSession],
  );

  const handleBreakoutSessionsDetails = useCallback(
    (payload: BreakoutSessionsDetailsPayload) => {
      console.log(
        "[useSubscribeToBreakoutSessionChanges] breakoutSessionsDetails",
      );

      updateBreakoutSessionParticipants(payload);
    },
    [updateBreakoutSessionParticipants],
  );

  const handleParticipantsMovedToBreakoutSession = useCallback(
    (payload: BreakoutSessionsUpdatedPayload) => {
      console.log(
        "[useSubscribeToBreakoutSessionChanges] handleParticipantsMovedToBreakoutSession",
      );
      moveParticipantToBreakoutSession(payload);
    },
    [moveParticipantToBreakoutSession],
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }
    socketClient.emit("iWantBreakoutSessionsDetails", {
      sessionId: sessionId,
    });

    socketClient.on("breakoutSessionsCreated", handleBreakoutSessionsCreated);
    socketClient.on("breakoutSessionClosed", handleBreakoutSessionClosed);
    socketClient.on(
      "participantsMovedToBreakoutSession",
      handleParticipantsMovedToBreakoutSession,
    );
    socketClient.on("breakoutSessionsDetails", handleBreakoutSessionsDetails);

    return () => {
      console.log("[useSubscribeToBreakoutSessionChanges] disconnecting");
      socketClient.off(
        "breakoutSessionsCreated",
        handleBreakoutSessionsCreated,
      );
      socketClient.off("breakoutSessionClosed", handleBreakoutSessionClosed);
      socketClient.off(
        "breakoutSessionsDetails",
        handleBreakoutSessionsDetails,
      );
    };
  }, [
    enabled,
    handleBreakoutSessionClosed,
    handleBreakoutSessionsCreated,
    handleBreakoutSessionsDetails,
    handleParticipantsMovedToBreakoutSession,
    socketClient,
    sessionId,
  ]);
}
